import { Injector } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  ButtonActionType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { take } from 'rxjs';
import { AccountService } from '../../../services/account-service/account-service.service';
import { TransactionState } from '../../../state/transaction.state';
import { showAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleRedirection } from '../cta-redirection.utils';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';

type Deps = {
  store: Store;
  alertHandler: AlertHandlerService;
  accountService: AccountService;
  ctaButtonSignalService: CtaButtonSignalService;
};

const getDeps = (injector: Injector): Deps => ({
  store: injector.get(Store),
  alertHandler: injector.get(AlertHandlerService),
  accountService: injector.get(AccountService),
  ctaButtonSignalService: injector.get(CtaButtonSignalService),
});

export const cancelPendingWithdrawal = (widgetProperties: BaseWidgetProperties, injector: Injector) => {
  const { store, alertHandler, accountService, ctaButtonSignalService } = getDeps(injector);

  const transactionState = store.selectSnapshot(TransactionState.getTransactions);
  const withdrawalId = transactionState?.currentTransactionId;

  if (!withdrawalId) {
    showAlertFromCode(alertHandler, {
      title: 'Error',
      type: 'error',
      name: 'Withdrawal ID Missing',
      description: 'Withdrawal ID is required to cancel pending withdrawal',
    });
    return;
  }

  const ctaAction = widgetProperties.buttonActionType as string;

  // disable button on click
  ctaButtonSignalService.setSignal({ [ctaAction]: false });

  accountService
    .removeScheduledPayment(withdrawalId)
    .pipe(take(1))
    .subscribe({
      next: () => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        handleRedirection(
          widgetProperties,
          injector,
          ActionButtonOutcomesType.SUCCESS,
          true,
          false,
          ButtonActionType.CANCEL_PENDING_WITHDRAWAL,
        );
      },
      error: () => {
        ctaButtonSignalService.setSignal({ [ctaAction]: true });
        handleRedirection(
          widgetProperties,
          injector,
          ActionButtonOutcomesType.FAILURE,
          true,
          false,
          ButtonActionType.CANCEL_PENDING_WITHDRAWAL,
        );
        Sentry.addBreadcrumb({
          category: 'Cancel Pending Withdrawal',
          message: 'Failed to cancel pending withdrawal',
          level: 'error',
        });
      },
    });
};
